import { supabase } from '../lib/supabase';
import { fetchAllPages } from '../lib/fetchAllPages';
import { isTelaOnline } from '../lib/telaStatus';
import { validarArquivoLogo } from '../lib/logoPlataforma';
import type { Empresa } from '../models/empresa.model';

import { planosService } from './planosService';
import {
  TAMANHO_PAGINA_AUDITORIA,
  type EmpresaStats, type HeartbeatLog, type TelaGlobal, type MetricasLicencas, type TokenAtivacao,
  type WatchdogLogEntry, type TenantUser, type GrupoTela, type RegistroAuditoria,
  type FiltrosAuditoria, type PaginaAuditoria, type PlaylistResumo, type ResultadoClonePlaylist,
  type ResultadoLote, type EstiloAviso, type AlteracoesRecursos,
  type ModuloPlataforma, type ExcecaoModulo,
  type Alerta, type AlertasConfig,
} from '../models/admin.model';
import type { PdvConfigAdminInput } from '../models/pdv.model';
import { traduzirErroAdmin } from '../lib/erroAdmin';
export type {
  EmpresaStats, HeartbeatLog, TelaGlobal, MetricasLicencas, TokenAtivacao, WatchdogLogEntry, TenantUser,
  GrupoTela, RegistroAuditoria, FiltrosAuditoria, PaginaAuditoria, StatusAuditoria, OrigemAuditoria,
  PlaylistResumo, ResultadoClonePlaylist,
  ResultadoLote, EstiloAviso, RecursoLote, AlteracoesRecursos,
  ModuloPlataforma, ExcecaoModulo, MODULOS_PLATAFORMA, ROTULO_MODULO,
  Alerta, AlertasConfig,
} from '../models/admin.model';

/**
 * `telas.ultimo_watchdog_log` é jsonb: pode chegar como array de eventos, objeto, texto ou null.
 * HeartbeatLog.log é texto para exibição — um array renderizado direto no JSX derruba o React.
 */
function resumirLog(valor: unknown): string | null {
  if (valor === null || valor === undefined) return null;
  return typeof valor === 'string' ? valor : JSON.stringify(valor);
}

/** Valor entre aspas para filtros PostgREST (.or): escapa `\` e `"`; vírgula e parêntese ficam seguros. */
function valorFiltro(texto: string): string {
  return `"${texto.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
}

/** Escapa curingas de LIKE (`%`, `_`, `\`) para filtrar por prefixo literal. */
function prefixoLike(texto: string): string {
  return `${texto.replace(/[\\%_]/g, c => `\\${c}`)}%`;
}

export const adminService = {
  async buscarPdvConfig(empresaId: string): Promise<PdvConfigAdminInput & { ativo: boolean } | null> {
    const { data, error } = await supabase.from('pdv_config_empresa').select('id_partner,cod_store,mesas,ativo').eq('empresa_id', empresaId).maybeSingle();
    if (error) throw error;
    return data as (PdvConfigAdminInput & { ativo: boolean }) | null;
  },

  async salvarPdvConfig(empresaId: string, dados: PdvConfigAdminInput): Promise<void> {
    const { error } = await supabase.rpc('admin_salvar_pdv_config' as never, {
      p_empresa_id: empresaId, p_id_partner: dados.id_partner, p_cod_store: dados.cod_store,
      p_mesas: dados.mesas, p_ativo: dados.ativo,
    } as never);
    if (error) throw traduzirErroAdmin(error);
  },

  async criarEmpresa(input: {
    nome_fantasia: string;
    cnpj:          string | null;
    plano:         Empresa['plano'];
    limite_telas:  number;
  }): Promise<Empresa> {
    const { data, error } = await supabase.rpc('admin_criar_empresa' as never, {
      p_nome_fantasia: input.nome_fantasia, p_cnpj: input.cnpj,
      p_plano: input.plano, p_limite_telas: input.limite_telas,
    } as never);
    if (error) throw traduzirErroAdmin(error);
    return data as Empresa;
  },

  async uploadLogoPlataforma(file: File): Promise<string> {
    const erroArquivo = validarArquivoLogo(file);
    if (erroArquivo) throw new Error(erroArquivo);
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Sessão expirada');
    const corpo = new FormData();
    corpo.append('arquivo', file);
    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-upload-logo`, {
      method: 'POST', headers: { Authorization: `Bearer ${session.access_token}` }, body: corpo,
    });
    const result = await res.json().catch(() => ({})) as { url?: string; error?: string };
    if (!res.ok || !result.url) throw new Error(result.error ?? 'Erro ao enviar o logo');
    return result.url;
  },


  async broadcastForceUpdate(version: string): Promise<void> {
    const { error } = await supabase.rpc('admin_forcar_atualizacao' as never, { p_version: version } as never);
    if (error) throw traduzirErroAdmin(error);
  },

  /** Busca todas as empresas com contagem de telas (super_admin bypassa RLS) */
  async listarEmpresas(busca?: string): Promise<EmpresaStats[]> {
    let query = supabase
      .from('empresas')
      .select('*')
      .order('criado_em', { ascending: false });

    if (busca) {
      // Entre aspas: vírgula, parêntese ou aspas da busca quebravam a sintaxe do filtro .or().
      const valor = valorFiltro(`%${busca}%`);
      query = query.or(`nome_fantasia.ilike.${valor},cnpj.ilike.${valor}`);
    }

    const { data: empresasRaw2, error } = await query;
    if (error) throw error;
    const empresas = (empresasRaw2 ?? []) as Empresa[];

    // Paginado: o PostgREST corta em 1000 linhas e erro de leitura não pode virar "0 telas".
    const telas = await fetchAllPages<{ empresa_id: string; ultima_conexao: string | null }>((from, to) =>
      supabase
        .from('telas')
        .select('empresa_id, ultima_conexao')
        .order('id', { ascending: true })
        .range(from, to),
    );

    // Presença pelo heartbeat (ultima_conexao), não por telas.status — que só
    // vira offline pelo cron ou pelo pagehide (TV sem energia ficava "online").
    const telasPorEmpresa: Record<string, { total: number; online: number }> = {};
    telas.forEach((t) => {
      if (!telasPorEmpresa[t.empresa_id]) {
        telasPorEmpresa[t.empresa_id] = { total: 0, online: 0 };
      }
      telasPorEmpresa[t.empresa_id].total++;
      if (isTelaOnline(t.ultima_conexao)) telasPorEmpresa[t.empresa_id].online++;
    });

    return empresas.map(e => ({
      ...e,
      total_telas:  telasPorEmpresa[e.id]?.total  ?? 0,
      telas_online: telasPorEmpresa[e.id]?.online ?? 0,
    }));
  },

  /** Métricas globais consolidadas */
  async metricas(): Promise<{
    mrr:            number;
    totalTelas:     number;
    telasOnline:    number;
    clientesAtivos: number;
  }> {
    const [{ data: empresasRaw, error: empresasError }, telas] = await Promise.all([
      supabase.from('empresas').select('plano, status'),
      fetchAllPages<{ ultima_conexao: string | null }>((from, to) =>
        supabase.from('telas').select('ultima_conexao').order('id', { ascending: true }).range(from, to),
      ),
    ]);
    if (empresasError) throw empresasError;

    const empresas = (empresasRaw ?? []) as Array<{ plano: string; status: string }>;

    const MRR_PLANO = await planosService.mapaPrecos();

    const mrr            = empresas.filter(e => e.status === 'ativo').reduce((acc, e) => acc + (MRR_PLANO[e.plano as keyof typeof MRR_PLANO] ?? 0), 0);
    const clientesAtivos = empresas.filter(e => e.status === 'ativo').length;
    const totalTelas     = telas.length;
    const telasOnline    = telas.filter(t => isTelaOnline(t.ultima_conexao)).length;

    return { mrr, totalTelas, telasOnline, clientesAtivos };
  },

  /** Últimos heartbeats de todas as telas da rede */
  async heartbeats(limite = 50): Promise<HeartbeatLog[]> {
    const { data, error } = await supabase
      .from('telas')
      .select('id, nome, status, ultima_conexao, ultimo_watchdog_log, telemetria_hw, empresa_id, empresas(nome_fantasia)')
      .order('ultima_conexao', { ascending: false })
      .limit(limite);

    if (error) throw error;

    type TelemetriaHw = { cpu_pct: number; ram_total_mb: number; ram_livre_mb: number; disco_total_gb: number; disco_livre_gb: number; uptime_min: number; app_version: string; ts: string; };
    type TelaRow = { id: string; nome: string; status: string; ultima_conexao: string | null; ultimo_watchdog_log: unknown; telemetria_hw: TelemetriaHw | null; empresas: { nome_fantasia: string } | null; };
    return ((data ?? []) as TelaRow[]).map(t => ({
      tela_id:        t.id,
      tela_nome:      t.nome,
      empresa_nome:   t.empresas?.nome_fantasia ?? '—',
      status:         t.status,
      ultima_conexao: t.ultima_conexao,
      log:            resumirLog(t.ultimo_watchdog_log),
      telemetria_hw:  t.telemetria_hw,
    }));
  },

  /** Altera plano e limite de telas de uma empresa */
  async alterarPlano(empresaId: string, plano: string, limiteTelas: number): Promise<void> {
    const { error } = await supabase.rpc('admin_alterar_plano' as never, {
      p_empresa_id: empresaId, p_plano: plano, p_limite_telas: limiteTelas,
    } as never);
    if (error) throw traduzirErroAdmin(error);
  },

  /**
   * Provisiona a tela única da empresa recém-criada.
   * Chamado automaticamente após criar empresa no NovaEmpresaModal.
   * Idempotente: se já existir uma tela para a empresa, não cria outra.
   */
  async provisionarTela(empresaId: string, nomeEmpresa: string): Promise<void> {
    const { error } = await supabase.rpc('admin_provisionar_tela' as never, {
      p_empresa_id: empresaId, p_nome_empresa: nomeEmpresa,
    } as never);
    if (error) throw traduzirErroAdmin(error);
  },

  /** Suspende ou ativa uma empresa e emite broadcast para bloquear as telas */
  async alterarStatus(empresaId: string, status: 'ativo' | 'suspenso'): Promise<void> {
    const { error } = await supabase.rpc('admin_alterar_status' as never, {
      p_empresa_id: empresaId, p_status: status,
    } as never);
    if (error) throw traduzirErroAdmin(error);
  },

  /**
   * Emite admin_cmd conta_suspensa em cada canal_tela_{id} da empresa.
   * O Player redireciona para /bloqueado ao receber o comando.
   */
  async suspenderCliente(empresaId: string): Promise<void> {
    const { error } = await supabase.rpc('admin_suspender_cliente' as never, {
      p_empresa_id: empresaId,
    } as never);
    if (error) throw traduzirErroAdmin(error);
  },

  // ── Licenças e Telas ───────────────────────────────────────────────────────

  /** Lista todas as telas da rede com dados da empresa vinculada */
  async listarTelasGlobal(): Promise<TelaGlobal[]> {
    type Row = {
      id: string; nome: string; localizacao: string | null;
      status: string; layout_atual: string; ultima_conexao: string | null;
      ultimo_watchdog_log: unknown; empresa_id: string; revogada: boolean;
      empresas: { nome_fantasia: string } | null;
    };
    // Paginado: com mais de 1000 telas a 1001ª não entrava na lista em que o admin revoga licença.
    const rows = await fetchAllPages<Row>((from, to) =>
      supabase
        .from('telas')
        .select('id, nome, localizacao, status, layout_atual, ultima_conexao, ultimo_watchdog_log, empresa_id, revogada, empresas(nome_fantasia)')
        .order('status', { ascending: true })
        .order('ultima_conexao', { ascending: false })
        .order('id', { ascending: true })
        .range(from, to) as unknown as PromiseLike<{ data: Row[] | null; error: unknown }>,
    );
    return rows.map(t => ({
      id:              t.id,
      nome:            t.nome,
      localizacao:     t.localizacao,
      status:          t.status as TelaGlobal['status'],
      layout_atual:    t.layout_atual as TelaGlobal['layout_atual'],
      ultima_conexao:  t.ultima_conexao,
      watchdog_log:    t.ultimo_watchdog_log,
      empresa_id:      t.empresa_id,
      empresa_nome:    t.empresas?.nome_fantasia ?? '—',
      revogada:        t.revogada ?? false,
    }));
  },

  /** Métricas consolidadas para o módulo de Licenças */
  async metricasLicencas(): Promise<MetricasLicencas> {
    const [
      { data: empresasRaw, error: empresasError },
      telas,
      { count: alertasWatchdog, error: watchdogError },
    ] = await Promise.all([
      supabase.from('empresas').select('limite_telas, status'),
      fetchAllPages<{ ultima_conexao: string | null }>((from, to) =>
        supabase.from('telas').select('ultima_conexao').order('id', { ascending: true }).range(from, to),
      ),
      supabase.from('watchdog_logs')
        .select('id', { count: 'exact', head: true })
        .gte('criado_em', new Date(Date.now() - 86_400_000).toISOString()),
    ]);
    if (empresasError) throw empresasError;
    if (watchdogError) throw watchdogError;

    const empresas = (empresasRaw ?? []) as Array<{ limite_telas: number; status: string }>;

    const licencasTotais  = empresas.filter(e => e.status === 'ativo').reduce((acc, e) => acc + e.limite_telas, 0);
    const telasOnline     = telas.filter(t => isTelaOnline(t.ultima_conexao)).length;
    const telasOffline    = telas.length - telasOnline;

    return { licencasTotais, telasOnline, telasOffline, alertasWatchdog: alertasWatchdog ?? 0 };
  },

  /** Gera um token de ativação de 6 caracteres (0-9A-Z) para uma empresa */
  async gerarToken(empresaId: string): Promise<string> {
    const { data, error } = await supabase.rpc('admin_gerar_token' as never, { p_empresa_id: empresaId } as never);
    if (error) throw traduzirErroAdmin(error);
    return data as string;
  },

  /** Lista tokens de ativação de uma empresa (não expirados) */
  async listarTokens(empresaId: string): Promise<TokenAtivacao[]> {
    const { data, error } = await supabase
      .from('tokens_ativacao')
      .select('*')
      .eq('empresa_id', empresaId)
      .order('criado_em', { ascending: false })
      .limit(10);
    if (error) throw error;
    return ((data ?? []) as TokenAtivacao[]);
  },

  /** Dispara reload remoto via Broadcast no canal core da tela */
  async forcarRestart(telaId: string): Promise<void> {
    const { error } = await supabase.rpc('admin_forcar_restart' as never, { p_tela_id: telaId } as never);
    if (error) throw traduzirErroAdmin(error);
  },

  /** Restaura uma licença revogada: permite que a TV volte a funcionar */
  async restaurarLicenca(telaId: string): Promise<void> {
    const { error } = await supabase.rpc('admin_restaurar_licenca' as never, { p_tela_id: telaId } as never);
    if (error) throw traduzirErroAdmin(error);
  },

  /** Revoga licença: persiste revogada=true, bloqueia a TV via broadcast */
  async revogarLicenca(telaId: string): Promise<void> {
    const { error } = await supabase.rpc('admin_revogar_licenca' as never, { p_tela_id: telaId } as never);
    if (error) throw traduzirErroAdmin(error);
  },

  // ── Gestão de usuários tenant ──────────────────────────────────────────────

  async listarUsuariosEmpresa(empresaId: string): Promise<TenantUser[]> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Sessão expirada');
    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-tenant-users`,
      {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ action: 'list', empresa_id: empresaId }),
      }
    );
    const result = await res.json();
    if (!res.ok) throw new Error(result.error ?? 'Erro ao listar usuários');
    return result.users ?? [];
  },

  async criarUsuarioTenant(email: string, password: string, empresaId: string): Promise<void> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Sessão expirada');
    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-tenant-user`,
      {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ email, password, empresa_id: empresaId }),
      }
    );
    const result = await res.json();
    if (!res.ok) throw new Error(result.error ?? 'Erro ao criar usuário');
  },

  async atualizarUsuarioTenant(
    userId: string,
    updates: { email?: string; password?: string },
    empresaId: string,
  ): Promise<void> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Sessão expirada');
    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-tenant-users`,
      {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ action: 'update', empresa_id: empresaId, user_id: userId, ...updates }),
      }
    );
    const result = await res.json();
    if (!res.ok) throw new Error(result.error ?? 'Erro ao atualizar usuário');
  },

  async removerUsuarioTenant(userId: string, empresaId: string): Promise<void> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Sessão expirada');
    const res = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/manage-tenant-users`,
      {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ action: 'delete', empresa_id: empresaId, user_id: userId }),
      }
    );
    const result = await res.json();
    if (!res.ok) throw new Error(result.error ?? 'Erro ao remover usuário');
  },

  /** Últimos logs do Watchdog (tabela centralizada) */
  async watchdogLogs(limite = 30): Promise<WatchdogLogEntry[]> {
    const { data, error } = await supabase
      .from('watchdog_logs')
      .select('*')
      .order('criado_em', { ascending: false })
      .limit(limite);
    if (error) throw error;
    return ((data ?? []) as WatchdogLogEntry[]);
  },

  async buscarConfigSaas(tabela: string): Promise<Record<string, unknown> | null> {
    const { data, error } = await supabase
      .from(tabela as never)
      .select('*')
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    return data as Record<string, unknown> | null;
  },

  async salvarConfigSaas(
    tabela:  string,
    id:      string | null,
    payload: Record<string, unknown>,
  ): Promise<void> {
    const { error } = await supabase.rpc('admin_salvar_config_saas' as never, {
      p_tabela: tabela, p_id: id, p_payload: payload,
    } as never);
    if (error) throw traduzirErroAdmin(error);
  },

  async verificarSaudeDb(): Promise<boolean> {
    const { error } = await supabase
      .from('empresas')
      .select('id', { count: 'exact', head: true });
    return !error;
  },

  /** Auth: obter a sessão atual só falha se o serviço estiver fora. */
  async verificarSaudeAuth(): Promise<boolean> {
    try {
      const { error } = await supabase.auth.getSession();
      return !error;
    } catch {
      return false;
    }
  },

  /**
   * Storage: fetch direto ao endpoint — qualquer resposta HTTP abaixo de 500 (mesmo 401) = serviço
   * operacional; só falha de rede/5xx é "fora". Evita o cliente Supabase (loga 400 por falta de service_role).
   */
  async verificarSaudeStorage(): Promise<boolean> {
    try {
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/bucket`, { method: 'GET' });
      return res.status < 500;
    } catch {
      return false;
    }
  },

  /**
   * Realtime: abre um canal temporário e espera SUBSCRIBED por até `timeoutMs`.
   * Tópico único por checagem: com nome fixo, uma 2ª checagem logo após a 1ª pegava o canal ainda em
   * 'leaving' (removeChannel é assíncrono) e nunca conectava. `sinal` aborta a checagem em voo
   * (view desmontada ou nova carga): resolve false, remove o canal e cancela o timer.
   */
  verificarSaudeRealtime(sinal?: AbortSignal, timeoutMs = 3000): Promise<boolean> {
    return new Promise<boolean>(resolve => {
      if (sinal?.aborted) { resolve(false); return; }

      const canal = supabase.channel(`__health__${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
      let concluido = false;
      let timer: ReturnType<typeof setTimeout> | null = null;

      const encerrar = (ok: boolean) => {
        if (concluido) return;
        concluido = true;
        if (timer) clearTimeout(timer);
        sinal?.removeEventListener('abort', aoAbortar);
        void supabase.removeChannel(canal);
        resolve(ok);
      };
      const aoAbortar = () => encerrar(false);

      timer = setTimeout(() => encerrar(false), timeoutMs);
      sinal?.addEventListener('abort', aoAbortar, { once: true });
      canal.subscribe(status => { if (status === 'SUBSCRIBED') encerrar(true); });
    });
  },

  // ── Grupos de telas (RPCs auditadas) ──────────────────────────────────────

  async listarGrupos(): Promise<GrupoTela[]> {
    type Row = {
      id: string; nome: string; descricao: string | null; criado_em: string;
      grupos_tela_membros: { count: number }[];
    };
    const { data, error } = await supabase
      .from('grupos_tela')
      .select('id, nome, descricao, criado_em, grupos_tela_membros(count)')
      .order('nome', { ascending: true });
    if (error) throw traduzirErroAdmin(error);
    return ((data ?? []) as unknown as Row[]).map(g => ({
      id:          g.id,
      nome:        g.nome,
      descricao:   g.descricao,
      criado_em:   g.criado_em,
      total_telas: g.grupos_tela_membros[0]?.count ?? 0,
    }));
  },

  async listarMembrosGrupo(grupoId: string): Promise<string[]> {
    const { data, error } = await supabase
      .from('grupos_tela_membros')
      .select('tela_id')
      .eq('grupo_id', grupoId);
    if (error) throw traduzirErroAdmin(error);
    return ((data ?? []) as { tela_id: string }[]).map(r => r.tela_id);
  },

  /** TVs ativas do alvo das ações em lote (rede inteira com grupoId null), contadas no servidor. */
  async contarTelasAlvo(grupoId: string | null): Promise<number> {
    const { data, error } = await supabase.rpc('admin_contar_telas_alvo' as never, { p_grupo_id: grupoId } as never);
    if (error) throw traduzirErroAdmin(error);
    return Number(data ?? 0);
  },

  async criarGrupo(nome: string, descricao: string | null): Promise<string> {
    const { data, error } = await supabase.rpc('admin_criar_grupo' as never, { p_nome: nome, p_descricao: descricao } as never);
    if (error) throw traduzirErroAdmin(error);
    return data as string;
  },

  async renomearGrupo(grupoId: string, nome: string, descricao: string | null): Promise<void> {
    const { error } = await supabase.rpc('admin_renomear_grupo' as never, { p_id: grupoId, p_nome: nome, p_descricao: descricao } as never);
    if (error) throw traduzirErroAdmin(error);
  },

  async excluirGrupo(grupoId: string): Promise<void> {
    const { error } = await supabase.rpc('admin_excluir_grupo' as never, { p_id: grupoId } as never);
    if (error) throw traduzirErroAdmin(error);
  },

  async definirMembrosGrupo(grupoId: string, telaIds: string[]): Promise<void> {
    const { error } = await supabase.rpc('admin_definir_membros_grupo' as never, { p_id: grupoId, p_tela_ids: telaIds } as never);
    if (error) throw traduzirErroAdmin(error);
  },

  // ── Módulos por plano (S4a) ────────────────────────────────────────────────

  async listarModulosPlanos(): Promise<Record<string, ModuloPlataforma[]>> {
    const { data, error } = await supabase
      .from('planos_modulos')
      .select('plano_slug, modulo')
      .order('modulo', { ascending: true });
    if (error) throw traduzirErroAdmin(error);
    const mapa: Record<string, ModuloPlataforma[]> = {};
    for (const row of (data ?? []) as { plano_slug: string; modulo: ModuloPlataforma }[]) {
      (mapa[row.plano_slug] ??= []).push(row.modulo);
    }
    return mapa;
  },

  async definirModulosPlano(slug: string, modulos: ModuloPlataforma[]): Promise<void> {
    const { error } = await supabase.rpc('admin_definir_modulos_plano' as never, {
      p_plano: slug, p_modulos: modulos,
    } as never);
    if (error) throw traduzirErroAdmin(error);
  },

  async listarExcecoesEmpresa(empresaId: string): Promise<ExcecaoModulo[]> {
    const { data, error } = await supabase
      .from('empresas_modulos_excecao')
      .select('modulo, liberado, motivo, criado_em')
      .eq('empresa_id', empresaId)
      .order('modulo', { ascending: true });
    if (error) throw traduzirErroAdmin(error);
    return (data ?? []) as ExcecaoModulo[];
  },

  async definirExcecaoModulo(
    empresaId: string,
    modulo: ModuloPlataforma,
    liberado: boolean | null,
    motivo: string | null,
  ): Promise<void> {
    const { error } = await supabase.rpc('admin_definir_excecao_modulo' as never, {
      p_empresa_id: empresaId, p_modulo: modulo, p_liberado: liberado, p_motivo: motivo,
    } as never);
    if (error) throw traduzirErroAdmin(error);
  },

  async listarAlertas(aba: 'abertos' | 'resolvidos'): Promise<Alerta[]> {
    let query = supabase.from('alertas').select('*').order('aberto_em', { ascending: false });
    if (aba === 'abertos') query = query.is('resolvido_em', null);
    else query = query.not('resolvido_em', 'is', null).gte('resolvido_em', new Date(Date.now() - 7 * 86_400_000).toISOString());
    const { data, error } = await query;
    if (error) throw traduzirErroAdmin(error);
    return (data ?? []) as Alerta[];
  },

  async contarAlertasAbertos(): Promise<number> {
    const { count, error } = await supabase.from('alertas').select('id', { count: 'exact', head: true }).is('resolvido_em', null);
    if (error) throw traduzirErroAdmin(error);
    return count ?? 0;
  },

  async resolverAlerta(id: string): Promise<void> {
    const { error } = await supabase.rpc('admin_resolver_alerta' as never, { p_id: id } as never);
    if (error) throw traduzirErroAdmin(error);
  },

  async buscarAlertasConfig(): Promise<AlertasConfig | null> {
    const { data, error } = await supabase.from('alertas_config').select('*').maybeSingle();
    if (error) throw traduzirErroAdmin(error);
    return data as AlertasConfig | null;
  },

  async salvarAlertasConfig(config: AlertasConfig): Promise<void> {
    const { error } = await supabase.rpc('admin_salvar_alertas_config' as never, {
      p_offline_min: config.offline_min, p_janela_inicio: config.janela_inicio,
      p_janela_fim: config.janela_fim, p_pdv_erro_min: config.pdv_erro_min,
      p_watchdog_reinicios: config.watchdog_reinicios, p_watchdog_janela_min: config.watchdog_janela_min,
    } as never);
    if (error) throw traduzirErroAdmin(error);
  },

  // ── Auditoria (só leitura) ─────────────────────────────────────────────────

  /** Página de 50 registros, mais recentes primeiro; busca 51 para saber se há próxima. */
  async listarAuditoria(filtros: FiltrosAuditoria, pagina: number): Promise<PaginaAuditoria> {
    const inicio = pagina * TAMANHO_PAGINA_AUDITORIA;
    let query = supabase
      .from('admin_auditoria')
      .select('*')
      .order('criado_em', { ascending: false })
      .order('id', { ascending: false })
      .range(inicio, inicio + TAMANHO_PAGINA_AUDITORIA);
    if (filtros.empresaId) query = query.eq('empresa_id', filtros.empresaId);
    if (filtros.acao)      query = query.like('acao', prefixoLike(filtros.acao));
    if (filtros.status)    query = query.eq('status', filtros.status);
    if (filtros.de)        query = query.gte('criado_em', filtros.de);
    if (filtros.ate)       query = query.lt('criado_em', filtros.ate);
    const { data, error } = await query;
    if (error) throw traduzirErroAdmin(error);
    const linhas = (data ?? []) as RegistroAuditoria[];
    return {
      registros: linhas.slice(0, TAMANHO_PAGINA_AUDITORIA),
      temMais:   linhas.length > TAMANHO_PAGINA_AUDITORIA,
    };
  },

  // ── Conteúdo: clonar playlist entre empresas ───────────────────────────────

  async listarPlaylistsEmpresa(empresaId: string): Promise<PlaylistResumo[]> {
    type Row = { id: string; nome: string; playlist_itens: { count: number }[] };
    const { data, error } = await supabase
      .from('playlists')
      .select('id, nome, playlist_itens(count)')
      .eq('empresa_id', empresaId)
      .order('nome', { ascending: true });
    if (error) throw traduzirErroAdmin(error);
    return ((data ?? []) as unknown as Row[]).map(p => ({
      id: p.id, nome: p.nome, total_itens: p.playlist_itens[0]?.count ?? 0,
    }));
  },

  async clonarPlaylist(playlistId: string, empresaDestinoId: string, nome: string | null): Promise<ResultadoClonePlaylist> {
    const { data, error } = await supabase.rpc('admin_clonar_playlist' as never, {
      p_playlist_id: playlistId, p_empresa_destino_id: empresaDestinoId, p_nome: nome,
    } as never);
    if (error) throw traduzirErroAdmin(error);
    return data as ResultadoClonePlaylist;
  },

  // ── Operação em massa (rede toda = grupoId null) ───────────────────────────

  async reiniciarEmLote(grupoId: string | null): Promise<ResultadoLote> {
    const { data, error } = await supabase.rpc('admin_lote_reiniciar' as never, { p_grupo_id: grupoId } as never);
    if (error) throw traduzirErroAdmin(error);
    return data as ResultadoLote;
  },

  async enviarAvisoEmLote(grupoId: string | null, texto: string, estilo: EstiloAviso, duracaoSeg: number): Promise<ResultadoLote> {
    const { data, error } = await supabase.rpc('admin_lote_aviso' as never, {
      p_grupo_id: grupoId, p_texto: texto, p_estilo: estilo, p_duracao_seg: duracaoSeg,
    } as never);
    if (error) throw traduzirErroAdmin(error);
    return data as ResultadoLote;
  },

  async alterarRecursosEmLote(grupoId: string | null, alteracoes: AlteracoesRecursos): Promise<ResultadoLote> {
    const { data, error } = await supabase.rpc('admin_lote_recursos' as never, {
      p_grupo_id: grupoId, p_alteracoes: alteracoes,
    } as never);
    if (error) throw traduzirErroAdmin(error);
    return data as ResultadoLote;
  },

  async listarLandingLeads(limite = 100): Promise<LandingLead[]> {
    const { data, error } = await supabase
      .from('landing_leads')
      .select('*')
      .order('criado_em', { ascending: false })
      .limit(limite);

    if (error) throw error;
    return (data ?? []) as LandingLead[];
  },
};

export interface LandingLead {
  id:          string;
  nome:        string;
  restaurante: string | null;
  cidade:      string | null;
  whatsapp:    string;
  num_telas:   number | null;
  mensagem:    string | null;
  origem:      string;
  criado_em:   string;
}
